/**
 * El mapa de navegación del ERP.
 *
 * Agrupado **por dominio, no por tabla** (ERP-UX.md §1): quien trabaja
 * aquí piensa en «campo» o «facturación», no en `work_order` y
 * `invoice_registry`.
 *
 * Cada entrada declara el permiso que la habilita. Un menú que ofrece
 * opciones que el servidor va a rechazar enseña a la gente a desconfiar de
 * la pantalla; uno que las esconde deja claro qué puede hacer cada rol.
 * **La autorización real la decide el servidor**: esto es sólo la cortesía
 * de no enseñar puertas cerradas.
 */

import type { Permission } from '@fibwifire/domain/browser';

export interface NavItem {
  path: string;
  label: string;
  /** Permiso mínimo para que la opción aparezca. */
  permission: Permission;
  /** Atajo de dos teclas de ERP-UX.md §5: `g` seguido de esta letra. */
  shortcut?: string;
}

export interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

export const NAVIGATION: readonly NavGroup[] = [
  {
    id: 'inicio',
    label: 'Inicio',
    items: [{ path: '/', label: 'Panel', permission: 'customers.read' }],
  },
  {
    id: 'clientes',
    label: 'Clientes',
    items: [
      { path: '/clientes', label: 'Clientes', permission: 'customers.read', shortcut: 'c' },
      {
        path: '/administradores',
        label: 'Administradores de fincas',
        permission: 'customers.read',
      },
      { path: '/centros', label: 'Centros', permission: 'customers.read' },
    ],
  },
  {
    id: 'activos',
    label: 'Activos',
    items: [
      { path: '/parques', label: 'Parques propios', permission: 'assets.read' },
      { path: '/equipos', label: 'Equipos', permission: 'assets.read', shortcut: 'e' },
      { path: '/normativa', label: 'Normativa', permission: 'regulation.read' },
      { path: '/catalogo', label: 'Catálogo', permission: 'assets.read' },
    ],
  },
  {
    id: 'mantenimiento',
    label: 'Mantenimiento',
    items: [
      { path: '/contratos', label: 'Contratos', permission: 'maintenance.read' },
      { path: '/planificacion', label: 'Planificación', permission: 'maintenance.read' },
    ],
  },
  {
    id: 'campo',
    label: 'Campo',
    items: [
      { path: '/ordenes', label: 'Órdenes de trabajo', permission: 'fieldservice.read', shortcut: 'o' },
      { path: '/revisiones', label: 'Revisiones', permission: 'fieldservice.read' },
      { path: '/anomalias', label: 'Anomalías', permission: 'fieldservice.read' },
    ],
  },
  {
    id: 'taller',
    label: 'Taller',
    items: [{ path: '/taller', label: 'Equipos en taller', permission: 'workshop.read' }],
  },
  {
    id: 'almacen',
    label: 'Almacén',
    items: [
      { path: '/existencias', label: 'Existencias', permission: 'inventory.read' },
      { path: '/almacenes', label: 'Gestión de almacén', permission: 'inventory.read' },
      { path: '/movimientos', label: 'Movimientos', permission: 'inventory.read' },
    ],
  },
  {
    id: 'ventas',
    label: 'Ventas',
    items: [
      { path: '/alquileres', label: 'Alquileres', permission: 'sales.read' },
      { path: '/presupuestos', label: 'Presupuestos', permission: 'sales.read' },
      { path: '/albaranes', label: 'Albaranes', permission: 'sales.read' },
      { path: '/proformas', label: 'Proformas', permission: 'sales.read' },
    ],
  },
  {
    id: 'compras',
    label: 'Compras',
    items: [{ path: '/compras', label: 'Pedidos y recepciones', permission: 'purchasing.read' }],
  },
  {
    id: 'facturacion',
    label: 'Facturación',
    items: [
      { path: '/facturas', label: 'Facturas', permission: 'billing.read', shortcut: 'f' },
      { path: '/cobros', label: 'Cobros y remesas', permission: 'treasury.read' },
      { path: '/comisiones', label: 'Comisiones', permission: 'treasury.read' },
    ],
  },
  {
    id: 'crm',
    label: 'CRM',
    items: [
      { path: '/radar', label: 'Radar comercial', permission: 'crm.read' },
      { path: '/oportunidades', label: 'Oportunidades', permission: 'crm.read' },
    ],
  },
  {
    id: 'informes',
    label: 'Informes',
    items: [{ path: '/informes', label: 'Cuadros de mando', permission: 'reporting.read' }],
  },
  {
    id: 'configuracion',
    label: 'Configuración',
    items: [
      { path: '/configuracion', label: 'Empresa', permission: 'platform.read' },
      { path: '/usuarios', label: 'Usuarios', permission: 'identity.read' },
      { path: '/terminales', label: 'Terminales', permission: 'identity.manage_devices' },
    ],
  },
] as const;

/**
 * El menú que le corresponde a un conjunto de permisos.
 *
 * Un grupo del que no queda ninguna opción **desaparece**: dejar el
 * encabezado «Facturación» sin nada debajo sugiere que algo se ha roto.
 */
export function visibleNavigation(
  can: (permission: Permission) => boolean,
): NavGroup[] {
  return NAVIGATION.map((group) => ({
    ...group,
    items: group.items.filter((item) => can(item.permission)),
  })).filter((group) => group.items.length > 0);
}

/**
 * Grupo al que pertenece una ruta.
 *
 * Lo usa la barra lateral para mantener desplegado el grupo donde se está
 * trabajando. Sin esto, navegar dentro de una sección la cerraría y habría
 * que volver a abrirla en cada salto — el menú plegable saldría más caro
 * que el menú largo.
 */
export function activeGroupId(pathname: string): string | null {
  let best: { groupId: string; length: number } | null = null;

  for (const group of NAVIGATION) {
    for (const item of group.items) {
      if (item.path === '/') {
        if (pathname === '/') return group.id;
        continue;
      }
      if (pathname !== item.path && !pathname.startsWith(`${item.path}/`)) continue;
      if (!best || item.path.length > best.length) {
        best = { groupId: group.id, length: item.path.length };
      }
    }
  }

  return best?.groupId ?? null;
}

/**
 * Migas de pan para una ruta.
 *
 * Se derivan del mapa de navegación, no de trocear la URL: `/ordenes`
 * tiene que decir «Campo › Órdenes de trabajo», no «Ordenes».
 */
export interface Crumb {
  label: string;
  path: string | null;
}

export function breadcrumbsFor(pathname: string): Crumb[] {
  // La ruta más larga que sea prefijo de la actual: así `/clientes/abc`
  // hereda las migas de `/clientes` y añade lo suyo.
  let best: { group: NavGroup; item: NavItem } | null = null;

  for (const group of NAVIGATION) {
    for (const item of group.items) {
      if (item.path === '/') continue;
      if (pathname !== item.path && !pathname.startsWith(`${item.path}/`)) continue;
      if (!best || item.path.length > best.item.path.length) best = { group, item };
    }
  }

  if (!best) return [{ label: 'Panel', path: null }];

  const crumbs: Crumb[] = [
    { label: best.group.label, path: null },
    { label: best.item.label, path: pathname === best.item.path ? null : best.item.path },
  ];

  // Lo que cuelga por debajo se añade tal cual: son identificadores, y el
  // nombre de verdad lo pone la pantalla cuando ha cargado el dato.
  const rest = pathname.slice(best.item.path.length).split('/').filter(Boolean);
  for (const [index, segment] of rest.entries()) {
    const isLast = index === rest.length - 1;
    crumbs.push({
      label: decodeURIComponent(segment),
      path: isLast ? null : `${best.item.path}/${rest.slice(0, index + 1).join('/')}`,
    });
  }

  return crumbs;
}
